import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = { class: "grid grid-cols-1 gap-4 md:grid-cols-3" }
const _hoisted_4 = {
  for: "users",
  class: "mb-2 block text-sm font-medium text-gray-800"
}

import { computed, reactive, ref, Ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@/utils/validators';

import { useUiStore } from '@/stores';
import { RoleService, SelectOptionsService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { goBack } from '@/composables/useNavigation';

import {
    DefaultServiceResult,
    FormAction,
    Permission,
    RoleFormState,
    RoleModel,
    RoleServiceCreateResult,
    SelectOptionsServiceGetUsersResult,
} from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'RoleFormPage',
  props: {
    action: {
        type: String,
        default: 'view',
    },
},
  setup(__props) {

const route = useRoute();
const uiStore = useUiStore();

const props = __props;

const formState = reactive<RoleFormState>({
    name: '',
    users: [],
});

const rules = computed(() => ({
    name: { required, maxLength: maxLength(500) },
    users: {},
}));

const v$: Ref<Validation<ValidationArgs<RoleFormState>>> = useVuelidate(rules, formState);

const usersOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchUsers = async () => {
    try {
        const response = await SelectOptionsService.getUsers();
        usersOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchRole = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await RoleService.get<RoleModel>(id);
        formState.name = data.name;
        formState.users = data.users?.map((u) => u.id) || [];
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const onSubmit = async () => {
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) return;

    try {
        setLoading(true);

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const response = await RoleService.update<DefaultServiceResult, RoleFormState>(
                id,
                formState,
            );
            toast.success(response.data.message);
            v$.value.$reset();
            goBack({ name: 'ViewRoleById', params: { id } });
        } else {
            const response = await RoleService.create<RoleServiceCreateResult, RoleFormState>(
                formState,
            );
            toast.success(response.data.message);
            v$.value.$reset();
            goBack({ name: 'ViewRoleById', params: { id: response.data.result.id } });
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await fetchUsers();
    if (props.action === FormAction.EDIT) {
        await fetchRole(Number(route.params.id));
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('role.' + props.action + '.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ViewRoleById' } }),
          (
                            (props.action == _unref(FormAction).CREATE && _ctx.$can(_unref(Permission).CREATE_ROLE)) ||
                            (props.action == _unref(FormAction).EDIT && _ctx.$can(_unref(Permission).EDIT_ROLE))
                        )
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_UiTextInput, {
            modelValue: _unref(v$).name.$model,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).name.$model) = $event)),
            name: "name",
            type: "text",
            label: _ctx.$t('role.labels.name'),
            errors: _unref(v$).name.$errors,
            onBlur: _unref(v$).name.$touch
          }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
          _createElementVNode("div", null, [
            _createElementVNode("label", _hoisted_4, _toDisplayString(_ctx.$t('role.labels.users')), 1),
            _createVNode(_component_UiMultiselect, {
              id: "users",
              name: "users",
              label: "name",
              "value-prop": "id",
              modelValue: formState.users,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.users) = $event)),
              multiple: "",
              primitive: "",
              options: usersOptions.value
            }, null, 8, ["modelValue", "options"])
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ], 32))
}
}

})